/**
 * Mirrors `backend/src/content/types.ts`. Kept as a separate copy so each
 * service builds independently — change one, change the other.
 */

export interface Company {
  name: string;
  legalName: string;
  registryCode: string;
  tagline: string;
  location: string;
  phone: string;
  phoneHref: string;
  officeHours: string;
  availability: { open: boolean; label: string };
}

export interface Hero {
  eyebrow: string;
  headline: { lead: string; accent: string; trail: string };
  body: string;
  primaryAction: { label: string; href: string };
  secondaryAction: { label: string; href: string };
  loopNote: string;
  loopCore: string;
}

export interface Practice {
  id: string;
  node: string;
  tag: string;
  title: string;
  body: string;
  note: string;
  tools: string[];
}

export interface Station {
  step: string;
  name: string;
  title: string;
  body: string;
}

export interface Cycle {
  eyebrow: string;
  title: string;
  body: string;
  stations: Station[];
  returnLabel: string;
  cadence: string;
}

export interface CaseStudy {
  id: string;
  title: string;
  body: string;
  url: string;
  urlLabel: string;
  facts: Array<{ label: string; value: string }>;
  tools: string[];
}

export interface StackGroup {
  name: string;
  items: string[];
}

export interface Section {
  eyebrow: string;
  title: string;
  body?: string;
}

export interface ContactSection extends Section {
  formIntro: string;
}

export interface SiteContent {
  company: Company;
  hero: Hero;
  services: Section;
  practices: Practice[];
  cycle: Cycle;
  work: Section;
  caseStudies: CaseStudy[];
  stack: Section;
  stackGroups: StackGroup[];
  contact: ContactSection;
}

/* -- contact form -- */

export interface ContactPayload {
  name: string;
  email: string;
  company: string;
  message: string;
  /** Honeypot: always submitted empty by a real browser session. */
  website: string;
}

export interface ContactSuccess {
  id: string | null;
  received: true;
  message?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;

  /**
   * True when the message came from our own API and is therefore written for a
   * visitor, in their language. False when the response came from something in
   * between — an nginx 502, a captive portal, a gateway timeout — whose body is
   * an HTML error page, leaving `message` as a bare "Request failed (502)".
   *
   * Callers must not show an unexplained error to a visitor: it tells them
   * nothing and, on the contact form, it is the moment they most need an
   * alternative way to reach us.
   */
  readonly explained: boolean;

  constructor(
    status: number,
    message: string,
    fields: Record<string, string> = {},
    explained = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
    this.explained = explained;
  }
}
